import React, { useState, useEffect } from 'react';
import { 
  UserCheck, 
  Plus, 
  Search, 
  Phone, 
  Mail, 
  MapPin,
  Clock,
  Edit,
  Trash2,
  Truck,
  Stethoscope
} from 'lucide-react';
import { motion } from 'framer-motion';
import toast from 'react-hot-toast';

const getLocalDateString = () => {
  const now = new Date();
  return now.toLocaleDateString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
};

const getLocalTimeZone = () => {
  const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
  const offset = -new Date().getTimezoneOffset();
  const sign = offset >= 0 ? '+' : '-';
  const hours = String(Math.floor(Math.abs(offset) / 60)).padStart(2, '0');
  const mins = String(Math.abs(offset) % 60).padStart(2, '0');
  return `${tz} (GMT${sign}${hours}:${mins})`;
};

const StaffManagement = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [filterRole, setFilterRole] = useState('all');
  const [filterStatus, setFilterStatus] = useState('all');
  const [showAddModal, setShowAddModal] = useState(false);
  const [selectedStaff, setSelectedStaff] = useState(null);
  const [currentTime, setCurrentTime] = useState(new Date());

  const staff = [
    {
      id: 'S001',
      name: 'Dr. Sarah Johnson',
      role: 'Emergency Physician',
      email: 'sarah.johnson@emergencycare.com',
      phone: '+91 98765 43210',
      license: 'MBBS-12345',
      experience: '8 years',
      specialization: ['Emergency Medicine', 'Trauma Care'],
      assignedAmbulance: 'AMB-001',
      status: 'active',
      shift: 'Day Shift (8 AM - 8 PM)',
      location: 'Downtown Medical Center',
      certifications: ['ACLS', 'PALS', 'ATLS'],
      lastTraining: '2024-01-10',
      nextTraining: '2024-07-10'
    },
    {
      id: 'S002',
      name: 'Mike Wilson',
      role: 'Ambulance Driver',
      email: 'mike.wilson@emergencycare.com',
      phone: '+91 98765 43211',
      license: 'DL-67890',
      experience: '5 years',
      specialization: ['Emergency Driving', 'Vehicle Maintenance'],
      assignedAmbulance: 'AMB-002',
      status: 'active',
      shift: 'Night Shift (8 PM - 8 AM)',
      location: 'City General Hospital',
      certifications: ['Emergency Vehicle Operations', 'First Aid'],
      lastTraining: '2024-01-15',
      nextTraining: '2024-07-15'
    },
    {
      id: 'S003',
      name: 'Dr. Emily Chen',
      role: 'Emergency Physician',
      email: 'emily.chen@emergencycare.com',
      phone: '+91 98765 43212',
      license: 'MBBS-67890',
      experience: '6 years',
      specialization: ['Pediatric Emergency', 'Critical Care'],
      assignedAmbulance: 'AMB-004',
      status: 'active',
      shift: 'Day Shift (8 AM - 8 PM)',
      location: 'Suburban Clinic',
      certifications: ['ACLS', 'PALS', 'FCCS'],
      lastTraining: '2024-01-20',
      nextTraining: '2024-07-20'
    },
    {
      id: 'S004',
      name: 'John Smith',
      role: 'Paramedic',
      email: 'john.smith@emergencycare.com',
      phone: '+91 98765 43213',
      license: 'PAR-12345',
      experience: '4 years',
      specialization: ['Emergency Care', 'Patient Assessment'],
      assignedAmbulance: 'AMB-003',
      status: 'offline',
      shift: 'Night Shift (8 PM - 8 AM)',
      location: 'Uptown Medical Center',
      certifications: ['EMT-P', 'ACLS', 'PHTLS'],
      lastTraining: '2024-01-25',
      nextTraining: '2024-07-25'
    }
  ];

  const filteredStaff = staff.filter(member => {
    const matchesSearch = member.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         member.id.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         member.email.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesRole = filterRole === 'all' || member.role.toLowerCase().includes(filterRole.toLowerCase());
    const matchesStatus = filterStatus === 'all' || member.status === filterStatus;
    return matchesSearch && matchesRole && matchesStatus;
  });

  const getStatusColor = (status) => {
    switch (status) {
      case 'active': return 'status-active';
      case 'offline': return 'status-offline';
      default: return 'status-offline';
    }
  };

  const getStatusText = (status) => {
    switch (status) {
      case 'active': return 'On Duty';
      case 'offline': return 'Off Duty';
      default: return 'Unknown';
    }
  };

  const getRoleIcon = (role) => {
    if (role.includes('Driver')) return Truck;
    if (role.includes('Physician') || role.includes('Paramedic')) return Stethoscope;
    return UserCheck;
  };

  const handleAddStaff = () => {
    toast.success('Staff member added successfully!');
    setShowAddModal(false);
  };

  const handleViewDetails = (member) => {
    setSelectedStaff(member);
  };

  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentTime(new Date());
    }, 1000);
    return () => clearInterval(timer);
  }, []);

  return (
    <div className="p-8 bg-neutral-50 min-h-screen">
      <div className="mb-10">
        <h1 className="text-3xl font-bold text-neutral-900 mb-2 tracking-tight">Staff Management</h1>
        <p className="text-neutral-500">
          Manage medical staff, drivers, and their assignments
        </p>
        <div className="flex items-center space-x-4 mt-2 text-sm text-neutral-400">
          <Clock className="w-4 h-4" />
          <span>{currentTime.toLocaleTimeString()}</span>
          <span className="text-neutral-400">|</span>
          <span>{getLocalDateString()}</span>
          <span className="text-neutral-400">|</span>
          <span>{getLocalTimeZone()}</span>
        </div>
      </div>

      {/* Controls */}
      <div className="bg-white rounded-2xl shadow-card border border-neutral-100 p-6 mb-8">
        <div className="flex flex-col lg:flex-row gap-4 items-end">
          <div className="flex-1">
            <label className="block text-sm font-medium text-neutral-700 mb-2">
              Search Staff
            </label>
            <div className="relative">
              <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 text-neutral-300 w-5 h-5" />
              <input
                type="text"
                placeholder="Search by name, ID, or email..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="input-field pl-12 h-12 text-base"
              />
            </div>
          </div>
          <div className="min-w-[180px] w-full lg:w-auto">
            <label className="block text-sm font-medium text-neutral-700 mb-2">
              Filter by Role
            </label>
            <div className="relative">
              <select
                value={filterRole}
                onChange={(e) => setFilterRole(e.target.value)}
                className="input-field h-12 text-base pr-10 appearance-none bg-neutral-50 focus:bg-white cursor-pointer transition-all"
                style={{ WebkitAppearance: 'none', MozAppearance: 'none', appearance: 'none' }}
              >
                <option value="all">All Roles</option>
                <option value="physician">Physician</option>
                <option value="driver">Driver</option>
                <option value="paramedic">Paramedic</option>
              </select>
              <span className="pointer-events-none absolute right-4 top-1/2 transform -translate-y-1/2 text-neutral-400">
                <svg width="20" height="20" fill="none" viewBox="0 0 20 20"><path d="M6 8l4 4 4-4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/></svg>
              </span>
            </div>
          </div>
          <div className="min-w-[180px] w-full lg:w-auto">
            <label className="block text-sm font-medium text-neutral-700 mb-2">
              Filter by Status
            </label>
            <div className="relative">
              <select
                value={filterStatus}
                onChange={(e) => setFilterStatus(e.target.value)}
                className="input-field h-12 text-base pr-10 appearance-none bg-neutral-50 focus:bg-white cursor-pointer transition-all"
                style={{ WebkitAppearance: 'none', MozAppearance: 'none', appearance: 'none' }}
              >
                <option value="all">All Status</option>
                <option value="active">On Duty</option>
                <option value="offline">Off Duty</option>
              </select>
              <span className="pointer-events-none absolute right-4 top-1/2 transform -translate-y-1/2 text-neutral-400">
                <svg width="20" height="20" fill="none" viewBox="0 0 20 20"><path d="M6 8l4 4 4-4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/></svg>
              </span>
            </div>
          </div>
          <div>
            <button
              onClick={() => setShowAddModal(true)}
              className="btn-primary flex items-center space-x-2 h-12 px-6 text-base"
            >
              <Plus className="w-5 h-5" />
              <span>Add Staff</span>
            </button>
          </div>
        </div>
      </div>

      {/* Staff Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        {filteredStaff.map((member, index) => {
          const RoleIcon = getRoleIcon(member.role);
          return (
            <motion.div
              key={member.id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.1 }}
              className="card hover:shadow-lg transition-shadow duration-300"
            >
              <div className="flex items-start justify-between mb-4">
                <div className="flex items-center space-x-3">
                  <div className="w-10 h-10 bg-primary-50 rounded-xl flex items-center justify-center">
                    <RoleIcon className="w-5 h-5 text-primary-500" />
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-neutral-900">{member.name}</h3>
                    <p className="text-sm text-neutral-500">{member.role}</p>
                  </div>
                </div>
                <span className={getStatusColor(member.status)}>
                  {getStatusText(member.status)}
                </span>
              </div>
              <div className="grid grid-cols-2 gap-4 mb-4">
                <div>
                  <p className="text-sm font-medium text-neutral-500">Experience</p>
                  <p className="text-sm text-neutral-900">{member.experience}</p>
                </div>
                <div>
                  <p className="text-sm font-medium text-neutral-500">License</p>
                  <p className="text-sm text-neutral-900">{member.license}</p>
                </div>
              </div>
              <div className="space-y-3 mb-4">
                <div className="flex items-center space-x-2">
                  <Mail className="w-4 h-4 text-neutral-300" />
                  <span className="text-sm text-neutral-900">{member.email}</span>
                </div>
                <div className="flex items-center space-x-2">
                  <Phone className="w-4 h-4 text-neutral-300" />
                  <span className="text-sm text-neutral-900">{member.phone}</span>
                </div>
                <div className="flex items-center space-x-2">
                  <Clock className="w-4 h-4 text-neutral-300" />
                  <span className="text-sm text-neutral-900">{member.shift}</span>
                </div>
                <div className="flex items-center space-x-2">
                  <MapPin className="w-4 h-4 text-neutral-300" />
                  <span className="text-sm text-neutral-900">{member.location}</span>
                </div>
              </div>
              <div className="mb-4">
                <p className="text-sm font-medium text-neutral-500 mb-2">Specializations</p>
                <div className="flex flex-wrap gap-1">
                  {member.specialization.map((spec, idx) => (
                    <span key={idx} className="text-xs bg-primary-50 text-primary-700 px-2 py-1 rounded-full">
                      {spec}
                    </span>
                  ))}
                </div>
              </div>
              {member.assignedAmbulance && (
                <div className="mb-4 p-3 bg-accent-50 rounded-xl">
                  <p className="text-sm font-medium text-accent-900">Currently Assigned</p>
                  <p className="text-xs text-accent-700">Ambulance: {member.assignedAmbulance}</p>
                </div>
              )}
              <div className="flex items-center justify-between pt-4 border-t border-neutral-100">
                <button
                  onClick={() => handleViewDetails(member)}
                  className="text-sm text-primary-600 hover:text-primary-800 font-semibold"
                >
                  View Details
                </button>
                <div className="flex space-x-2">
                  <button className="p-2 text-neutral-300 hover:text-primary-500 transition-colors">
                    <Edit className="w-4 h-4" />
                  </button>
                  <button className="p-2 text-neutral-300 hover:text-red-500 transition-colors">
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </div>
            </motion.div>
          );
        })}
      </div>

      {/* Staff Details Modal */}
      {selectedStaff && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            className="bg-white rounded-xl shadow-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto"
          >
            <div className="flex items-center justify-between mb-4 p-6 border-b border-neutral-100">
              <h2 className="text-xl font-bold text-neutral-900">Staff Details</h2>
              <button
                onClick={() => setSelectedStaff(null)}
                className="text-neutral-300 hover:text-neutral-600"
              >
                ✕
              </button>
            </div>
            <div className="grid grid-cols-2 gap-6 p-6">
              <div>
                <h3 className="font-semibold text-neutral-900 mb-3">Personal Information</h3>
                <div className="space-y-2">
                  <p><span className="font-medium">Name:</span> {selectedStaff.name}</p>
                  <p><span className="font-medium">ID:</span> {selectedStaff.id}</p>
                  <p><span className="font-medium">Role:</span> {selectedStaff.role}</p>
                  <p><span className="font-medium">Email:</span> {selectedStaff.email}</p>
                  <p><span className="font-medium">Phone:</span> {selectedStaff.phone}</p>
                  <p><span className="font-medium">License:</span> {selectedStaff.license}</p>
                  <p><span className="font-medium">Experience:</span> {selectedStaff.experience}</p>
                </div>
              </div>
              <div>
                <h3 className="font-semibold text-neutral-900 mb-3">Work Information</h3>
                <div className="space-y-3">
                  <div>
                    <p className="font-medium text-neutral-700">Shift:</p>
                    <p className="text-sm text-neutral-900">{selectedStaff.shift}</p>
                  </div>
                  <div>
                    <p className="font-medium text-neutral-700">Location:</p>
                    <p className="text-sm text-neutral-900">{selectedStaff.location}</p>
                  </div>
                  <div>
                    <p className="font-medium text-neutral-700">Certifications:</p>
                    <div className="flex flex-wrap gap-1 mt-1">
                      {selectedStaff.certifications.map((cert, idx) => (
                        <span key={idx} className="text-xs bg-primary-50 text-primary-700 px-2 py-1 rounded-full">
                          {cert}
                        </span>
                      ))}
                    </div>
                  </div>
                  <div>
                    <p className="font-medium text-neutral-700">Training Schedule:</p>
                    <p className="text-xs text-neutral-400">Last: {selectedStaff.lastTraining}</p>
                    <p className="text-xs text-neutral-400">Next: {selectedStaff.nextTraining}</p>
                  </div>
                </div>
              </div>
            </div>
            <div className="mt-6 pt-4 border-t border-neutral-100 p-6">
              <button
                onClick={() => setSelectedStaff(null)}
                className="btn-primary"
              >
                Close
              </button>
            </div>
          </motion.div>
        </div>
      )}

      {/* Add Staff Modal */}
      {showAddModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            className="bg-white rounded-xl shadow-xl w-full max-w-md"
          >
            <h2 className="text-xl font-bold text-neutral-900 mb-4 p-6 border-b border-neutral-100">Add New Staff Member</h2>
            <div className="space-y-4 p-6">
              <div>
                <label className="block text-sm font-medium text-neutral-700 mb-2">
                  Full Name
                </label>
                <input type="text" className="input-field" placeholder="Dr. John Doe" />
              </div>
              <div>
                <label className="block text-sm font-medium text-neutral-700 mb-2">
                  Role
                </label>
                <select className="input-field">
                  <option>Emergency Physician</option>
                  <option>Ambulance Driver</option>
                  <option>Paramedic</option>
                  <option>Nurse</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-neutral-700 mb-2">
                  Email
                </label>
                <input type="email" className="input-field" placeholder="john.doe@emergencycare.com" />
              </div>
              <div>
                <label className="block text-sm font-medium text-neutral-700 mb-2">
                  Phone Number
                </label>
                <input type="tel" className="input-field" placeholder="+91 98765 43210" />
              </div>
              <div>
                <label className="block text-sm font-medium text-neutral-700 mb-2">
                  License Number
                </label>
                <input type="text" className="input-field" placeholder="MBBS-12345" />
              </div>
            </div>
            <div className="flex space-x-3 mt-6 p-6 border-t border-neutral-100">
              <button
                onClick={() => setShowAddModal(false)}
                className="flex-1 px-4 py-3 border border-neutral-200 rounded-xl text-neutral-700 hover:bg-neutral-50 transition-colors duration-200 font-medium"
              >
                Cancel
              </button>
              <button
                onClick={handleAddStaff}
                className="flex-1 btn-primary"
              >
                Add Staff
              </button>
            </div>
          </motion.div>
        </div>
      )}
    </div>
  );
};

export default StaffManagement; 